import { useApolloClient, useMutation } from '@apollo/client'
import {
  FetchViewsDocument,
  MoveViewsDocument,
  ViewConfigTypeEnum,
  type ViewFieldsFragment,
} from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import { getOrganizationIdFromPath, useCanForView } from '@salescore/client-common'
import type { ViewConfigKpiPivot } from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { ButtonWithTooltip, useBooleanState } from '@salescore/frontend-common'
import { message } from 'antd'
import { t } from 'i18next'
import { type ReactNode, useContext } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { kpiFormModifiedAtom, viewsContextAtom } from '../../../../recoil/view/atoms'
import { attachMetricToKpiConfigFromFormula } from '../../../../recoil/view/attachMetricToKpiConfig'
import { useStepStatus, useValidatedFormValue } from '../../../../recoil/view/hooks/kpiForm'
import { useViewConfigKpi } from '../../../../recoil/view/hooks/useViewConfigKpi'
import { ViewUIContext } from '../../ViewUIComponent'

export const useSaveKpiForm = ({ view }: { view: ViewFieldsFragment }) => {
  // eslint-disable-next-line sonarjs/no-unused-vars
  const [_formModified, setFormModified] = useRecoilState(kpiFormModifiedAtom)
  const validatedFormValue = useValidatedFormValue()
  const client = useApolloClient()
  // eslint-disable-next-line sonarjs/deprecation
  const loading = useBooleanState()
  const { updateView } = useRecoilValue(viewsContextAtom)
  const [moveView] = useMutation(MoveViewsDocument)
  const [, setKpiViewConfig] = useViewConfigKpi()
  const { onFinish } = useContext(ViewUIContext)
  const canUpdate = useCanForView('update', view)

  const { saveEnabled, errorItems } = useStepStatus()

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const saveKpiForm = async (): Promise<void> => {
    try {
      if (loading.isTrue) {
        // 保存中は重複実行しない
        return
      }
      if (!canUpdate || !saveEnabled) {
        message.error(<ValidationErrorMessage canUpdate={canUpdate} className="text-left" errorItems={errorItems} />)
        return
      }
      loading.setTrue()
      const { name, viewGroupId, dashboardIds, kpiConfig } = validatedFormValue
      const { data } = await client.query({
        query: FetchViewsDocument,
        variables: {
          organizationId: getOrganizationIdFromPath(),
          type: ViewConfigTypeEnum.KpiPivot,
        },
      })
      const dashboardViews = data.views

      if (name === undefined) {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO
        return
      }

      const saved = await updateView({
        id: view.id,
        name,
        config: kpiConfig.data,
      })
      if (viewGroupId !== undefined) {
        await moveView({
          variables: {
            organizationId: getOrganizationIdFromPath(),
            inputs: [
              {
                id: view.id,
                viewGroupId,
                rank: 0,
              },
            ],
          },
        })
      }
      const savedConfig = saved?.config
      if (savedConfig?.type === 'kpi') {
        // savedConfig は updateView (BE の attachMetricToView) を通ったレスポンスで、GraphQL の config は
        // JSON scalar のため型に露出しない formula.metric がランタイムに乗っている。client-api の型は
        // formula を metric なしで公開するので、その formula.metric を zod parse で型安全に読み取り面
        // (ViewConfigKpiWithMetric) へ持ち上げてから atom へ流す。初期 seed (initializeState) と同経路。
        setKpiViewConfig(attachMetricToKpiConfigFromFormula(savedConfig))
      }

      for (const dashboardView of dashboardViews) {
        const { config } = dashboardView
        if (config.type !== 'kpiPivot') {
          continue
        }
        // ダッシュボードがこのビューを含むとき
        if (config.kpis.map((x) => x.viewId).includes(view.id)) {
          // 今回の更新にも含まれていれば、何もしなくて良い
          if ((dashboardIds ?? []).includes(dashboardView.id)) {
            continue
          }
          // 今回の更新に含まれていなければ、削除する必要がある
          const newConfig: ViewConfigKpiPivot = {
            ...config,
            kpis: config.kpis.filter((x) => x.viewId !== view.id),
          }
          await updateView({
            id: dashboardView.id,
            config: newConfig,
          })
          continue
        }
        // 含まれていない時、今回の更新で含むようにしていれば、追加
        if ((dashboardIds ?? []).includes(dashboardView.id)) {
          const newConfig: ViewConfigKpiPivot = {
            ...config,
            kpis: [
              ...config.kpis,
              {
                viewId: view.id,
              },
            ],
          }
          await updateView({
            id: dashboardView.id,
            config: newConfig,
          })
        }
      }
      setFormModified(false)
      if (onFinish !== undefined) {
        onFinish()
      }
      void message.success(t(`設定を保存しました。`))
      Posthog.track(POSTHOG_EVENTS.save_kpi, { organizationId: getOrganizationIdFromPath(), config: saved?.config })
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      } else {
        void message.error(t(`エラーが発生しました。`))
      }
    } finally {
      loading.setFalse()
    }
  }

  return { saveKpiForm, loading }
}

export const KpiFormSaveButton = ({
  disabled,
  kpiSaveing,
  kpiSave,
  view,
}: {
  disabled: boolean
  /** KPIの保存ローディング状態（ショートカットキーでの保存状態を共有する）*/
  kpiSaveing: boolean
  /** KPIの保存実行 */
  kpiSave: () => void
  view: ViewFieldsFragment
}): ReactNode => {
  const canUpdate = useCanForView('update', view)
  const { saveEnabled, errorItems } = useStepStatus()
  return (
    <ButtonWithTooltip
      tooltipTitle={<ValidationErrorMessage canUpdate={canUpdate} errorItems={errorItems} />}
      showTooltip={!canUpdate || errorItems.length > 0}
      disabled={!canUpdate || !saveEnabled || disabled}
      type="primary"
      htmlType="submit"
      loading={kpiSaveing}
      onClick={kpiSave}
    >
      {t(`保存`)}
    </ButtonWithTooltip>
  )
}

/** KPI設定のバリデーションエラーメッセージ */
const ValidationErrorMessage = ({
  className,
  canUpdate,
  errorItems,
}: {
  className?: string
  canUpdate: boolean
  errorItems: Array<{ step: number; label: string }>
  // eslint-disable-next-line sonarjs/function-return-type
}): ReactNode => {
  if (!canUpdate) {
    return t(`権限がありません`)
  }
  return (
    <div className={className}>
      <p>{t('必須項目を入力してください')}</p>
      {errorItems.map(({ step, label }, index) => (
        <p key={`${step}-${index}`}>・{label}</p>
      ))}
    </div>
  )
}
